import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { InkEmptyState } from '@movable/ui';
import ErrorIcon from '@mui/icons-material/Error';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box } from '@mui/material';

import {
  isDrawerLockedAtom,
  isDrawerOpenAtom,
} from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import { useInAppNotificationFeed } from '@/providers/InAppNotificationFeedProvider';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';
import { Nullable } from '@/utils/globalTypes';

import NotificationFeedCell, {
  FeedItem,
  FeedItemAction,
} from './NotificationFeedCell';
import NotificationFeedCellSkeleton from './NotificationFeedCellSkeleton';
import NotificationFeedWrapper from './NotificationFeedWrapper';

type ItemActionMap = Record<FeedItemAction, () => void>;

const NotificationFeed = () => {
  const isDrawerLocked = useRecoilValue(isDrawerLockedAtom);
  const setIsDrawerOpen = useSetRecoilState(isDrawerOpenAtom);

  const {
    items,
    feedClient,
    hasNotificationItems,
    hasNextPage,
    hasError,
    isLoadingMoreNotificationFeed,
    isLoadingNotificationFeed,
    setFeedAnchorEl,
  } = useInAppNotificationFeed();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const handleActionClick = (item: FeedItem): ItemActionMap => ({
    markAsRead: () => {
      feedClient.markAsRead(item).catch(() => {
        snackbar({
          message: 'There was an error marking the notification as read.',
          severity: 'error',
        });
      });
    },
    markAsUnread: () => {
      feedClient.markAsUnread(item).catch(() => {
        snackbar({
          message: 'There was an error marking the notification as unread.',
          severity: 'error',
        });
      });
    },
    archive: () => {
      feedClient.markAsArchived(item).catch(() => {
        snackbar({
          message: 'There was an error archiving the notification.',
          severity: 'error',
        });
      });
    },
  });

  const handleFeedItemClick = (entityUrl: Nullable<string>) => {
    if (!entityUrl) {
      return snackbar({
        message: 'There was an error accessing this notification.',
        severity: 'error',
      });
    }

    if (!isDrawerLocked) {
      setIsDrawerOpen(false);
    }

    setFeedAnchorEl(null);
    feedClient.markAllAsRead();
    navigate(entityUrl);
  };

  if (isLoadingNotificationFeed) {
    return (
      <NotificationFeedWrapper>
        <Box
          sx={{
            '& > div + div': {
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <NotificationFeedCellSkeleton key={index} />
          ))}
        </Box>
      </NotificationFeedWrapper>
    );
  }

  if (hasError) {
    return (
      <NotificationFeedWrapper>
        <InkEmptyState
          icon={<ErrorIcon sx={{ fontSize: 64, color: 'neutral500' }} />}
          title="Sorry, something went wrong."
          description="There was an error loading your notifications feed. Please try again later."
          variant="page"
          sx={{ gap: 1 }}
        />
      </NotificationFeedWrapper>
    );
  }

  if (!hasNotificationItems) {
    return (
      <NotificationFeedWrapper>
        <InkEmptyState
          icon={
            <NotificationsIcon sx={{ fontSize: 64, color: 'neutral500' }} />
          }
          title="No Notifications"
          description="To see notifications go to your notification preferences and enable notifications for the messages you want to receive."
          variant="page"
          sx={{
            gap: 1,
            '& > .MuiBox-root': {
              mt: 1,
            },
          }}
          action={{
            title: 'Configure preferences',
            onClick: () => {
              setFeedAnchorEl(null);
              navigate(`/${clientId}/notifications`);
            },
          }}
        />
      </NotificationFeedWrapper>
    );
  }

  return (
    <NotificationFeedWrapper
      canFetchMore={
        hasNextPage &&
        !isLoadingMoreNotificationFeed &&
        !isLoadingNotificationFeed
      }
      onFetchMore={() => {
        feedClient.fetchNextPage();
      }}
    >
      {items.map((item) => (
        <NotificationFeedCell
          key={item.id}
          item={item}
          onItemActionClick={(action) => {
            handleActionClick(item)[action]();
          }}
          onItemClick={handleFeedItemClick}
        />
      ))}

      {hasNextPage && isLoadingMoreNotificationFeed && (
        <NotificationFeedCellSkeleton />
      )}
    </NotificationFeedWrapper>
  );
};

export default NotificationFeed;
